import { useMemo } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { useMyCollaborations } from "@/hooks/useCollaborations";
import { EscrowBalanceCard } from "@/components/marketplace";
import { formatMoney } from "@/lib/format";
import type { Collaboration, CollaborationStatus } from "@/hooks/useCollaborations";

/**
 * The money figure pinned to the foot of the sidebar.
 *
 * The design system calls this the escrow balance — "the one number that makes
 * the guarantee feel real". It is deliberately NOT labelled that here, because
 * escrow is not wired yet: F3 is unstarted, `EscrowAccount` exists in Prisma
 * but nothing funds it, and no money has moved. A sidebar reading "FONDOS EN
 * GARANTÍA" over a total nobody has deposited would be exactly the mistake the
 * provenance badges exist to prevent — a confident number about money that is
 * not there, shown to people who would find out.
 *
 * So it reports what is true today: the value committed across live
 * collaborations. When the payment clock lands, the labels flip to the design's
 * wording and the arithmetic below is already right.
 */

/**
 * States where a rate has been agreed and the work is still open.
 *
 * `NEGOTIATING` is excluded — an offer under discussion is not a commitment,
 * and counting it would let the figure move every time someone floats a number.
 * `RELEASED`, `CANCELLED` and `DISPUTED` are all closed, one way or another.
 */
const LIVE: ReadonlySet<CollaborationStatus> = new Set<CollaborationStatus>([
  "AGREED",
  "FUNDED",
  "SHIPPED",
  "IN_PROGRESS",
  "SUBMITTED",
  "REVISION_REQUESTED",
  "APPROVED",
  "PUBLISHED",
]);

interface Bucket {
  currency: string;
  total: number;
  count: number;
}

/**
 * Totals bucketed by currency, largest first.
 *
 * There is no single platform currency — a Uruguayan brand may pay an Argentine
 * creator — so amounts in different currencies are never added together. The
 * card shows the largest bucket and says how many others exist rather than
 * producing one meaningless number.
 */
function bucketByCurrency(
  collaborations: Collaboration[],
  side: "brand" | "creator",
): Bucket[] {
  const byCurrency = new Map<string, Bucket>();

  for (const c of collaborations) {
    if (!LIVE.has(c.status)) continue;
    const amount = side === "brand" ? c.money.brandTotal : c.money.creatorPayout;
    if (amount === null || amount === undefined) continue;

    const currency = c.money.currency || "USD";
    const bucket = byCurrency.get(currency) ?? { currency, total: 0, count: 0 };
    bucket.total += amount;
    bucket.count += 1;
    byCurrency.set(currency, bucket);
  }

  return [...byCurrency.values()].sort((a, b) => b.total - a.total);
}

export default function CollaborationBalanceCard({ isCreator }: { isCreator: boolean }) {
  const { t } = useTranslation();
  const { collaborations } = useMyCollaborations();

  const side = isCreator ? "creator" : "brand";
  const buckets = useMemo(
    () => bucketByCurrency(collaborations, side),
    [collaborations, side],
  );

  // Nothing live: render nothing rather than a zero. A zero balance in a
  // sidebar reads as a broken widget, and there is no action to take from it.
  if (buckets.length === 0) return null;

  const [primary, ...rest] = buckets;
  const note = [
    t(`marketplace.escrow.balance.${side}Note`, { count: primary.count }),
    rest.length > 0
      ? t("marketplace.escrow.balance.otherCurrencies", { count: rest.length })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <EscrowBalanceCard
      label={t(`marketplace.escrow.balance.${side}Label`)}
      amount={formatMoney(primary.total, primary.currency)}
      note={note}
    />
  );
}
